import { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { API_ENDPOINTS } from '@/constants';

const HealthCheck: React.FC = () => {
    const [serverStatus, setServerStatus] = useState<{
        status: string;
        message: string;
        version?: string;
    }>({
        status: 'checking',
        message: 'Checking server status...',
    });
    const [error, setError] = useState('');

    useEffect(() => {
        const checkServerStatus = async () => {
            try {
                const response = await fetch(API_ENDPOINTS.HEALTH, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                const status = response.status;
                const responseText = await response.text();

                try {
                    if (status === 200) {
                        const data = JSON.parse(responseText);
                        setServerStatus(data);
                        setError('');
                    } else {
                        throw new Error(responseText);
                    }
                } catch (err: unknown) {
                    const parseErr = err instanceof Error ? err : new Error(String(err));
                    throw new Error(`Failed to parse JSON: ${parseErr.message}`);
                }
            } catch (err) {
                setServerStatus({
                    status: 'error',
                    message: 'Server is not responding',
                });
                setError(err instanceof Error ? err.message : 'Failed to connect to server');
            }
        };

        void checkServerStatus();
    }, [API_ENDPOINTS.HEALTH]);

    const getStatusIcon = () => {
        if (serverStatus.status === 'ok') {
            let tooltipTitle = 'Server online';

            if (serverStatus.version) {
                tooltipTitle += `. App version: ${serverStatus.version}`;
            }

            return (
                <Tooltip title={tooltipTitle} placement="left" arrow>
                    <CheckCircleIcon sx={{ fontSize: 12, color: 'success.main' }} />
                </Tooltip>
            );
        }
        if (serverStatus.status === 'checking') {
            return (
                <Tooltip title="Checking server status..." placement="left" arrow>
                    <HourglassEmptyIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                </Tooltip>
            );
        }
        return (
            <Tooltip title="Server offline" placement="left" arrow>
                <ErrorIcon sx={{ fontSize: 12, color: 'error.main' }} />
            </Tooltip>
        );
    };

    return (
        <Box mt={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'text.secondary' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} data-testid="status-icon">
                {getStatusIcon()}
            </Box>
            {error && (
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default HealthCheck;
